import { Spinner } from './Spinner';
import { ComponentProps, forwardRef } from 'react';
import { tv, type VariantProps } from 'tailwind-variants';
import { twJoin } from 'tailwind-merge';
import { defaultInputClasses } from './utils';

export const buttonVariants = tv({
  slots: {
    wrapper: twJoin(
      'flex group justify-center items-center transition-all border-2',
      'tracking-wider font-aribaubold duration-200 cursor-pointer',
      defaultInputClasses,
    ),
    text: 'flex antialiased items-center justify-center w-full gap-3 duration-200 transition-all select-none',
  },
  variants: {
    disabled: {
      true: {
        wrapper: 'opacity-50',
      },
    },
    size: {
      small: {
        wrapper: 'px-4 py-2',
        text: 'text-sm',
      },
      medium: {
        wrapper: 'px-6 py-2',
        text: 'text-lg',
      },
    },
    color: {
      primary: {
        wrapper: 'border-primary-600 hover:border-primary-700 active:border-primary-600',
        text: 'text-white',
      },
      secondary: {
        wrapper:
          'bg-gray-600 border-gray-600 hover:bg-gray-700 hover:border-gray-700 active:bg-gray-600 active:border-gray-600',
        text: 'text-white',
      },
    },
    fill: {
      contained: {
        wrapper: 'bg-primary-600 hover:bg-primary-700 active:bg-primary-600',
      },
      outlined: {
        wrapper: 'bg-transparent hover:bg-primary-700 active:bg-primary-600',
        text: 'group-hover:text-white',
      },
      ghost: {
        wrapper: 'bg-transparent border-0 data-[checked]:bg-primary-600',
        text: 'text-primary-600 group-hover:text-primary-700 group-active:text-primary-700 group-data-[checked]:text-white ',
      },
    },
  },
  compoundVariants: [
    {
      fill: 'contained',
      color: 'secondary',
      class: {
        wrapper:
          'bg-gray-600 border-gray-600 hover:bg-gray-700 hover:border-gray-700 active:bg-gray-600 active:border-gray-600',
      },
    },
    {
      disabled: true,
      color: 'primary',
      fill: 'contained',
      class: {
        wrapper: 'hover:bg-primary-600 active:bg-primary-600',
      },
    },
    {
      disabled: true,
      color: 'primary',
      fill: 'outlined',
      class: {
        wrapper: 'hover:bg-transparent active:bg-transparent',
        text: 'group-hover:text-primary-600',
      },
    },
    {
      disabled: true,
      fill: 'ghost',
      class: {
        text: 'text-gray-400',
      },
    },
    {
      color: 'secondary',
      fill: 'outlined',
      class: {
        text: 'text-gray-600 active:text-gray-700',
      },
    },
    {
      color: 'primary',
      fill: 'outlined',
      class: {
        text: 'text-primary-600 active:text-primary-700',
      },
    },
  ],
  defaultVariants: {
    color: 'primary',
    size: 'medium',
    disabled: false,
    fill: 'contained',
  },
});

type ButtonVariants = VariantProps<typeof buttonVariants>;

interface ButtonProps<T extends React.ElementType> extends ButtonVariants {
  as?: T;
  children?: React.ReactNode;
  loading?: boolean;
  inputRef?: React.ForwardedRef<HTMLButtonElement>;
  textClassName?: string;
}

export function Button<T extends React.ElementType = 'button'>({
  as,
  loading = false,
  children,
  className,
  textClassName,
  inputRef,
  ...props
}: ButtonProps<T> &
  Omit<React.ComponentPropsWithoutRef<T>, keyof ButtonProps<T>>): React.ReactElement {
  const disabled = props.disabled || loading;

  const Component = as || 'button';

  const { wrapper, text } = buttonVariants({ ...props, disabled });

  return (
    <Component className={wrapper({ className })} {...props} disabled={disabled} ref={inputRef}>
      <span className={text({ className: textClassName })}>
        {loading ? <Spinner /> : null}
        {children}
      </span>
    </Component>
  );
}

// For cenarios where we need to forward the ref to the button element
export const ForwardedButton = forwardRef<HTMLButtonElement, ComponentProps<typeof Button>>(
  (props, ref) => (
    <Button inputRef={ref} {...props}>
      {props.children}
    </Button>
  ),
);
